import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, CreditCard, Headphones, LogIn } from "lucide-react";
import { useAuth, useClerk, UserButton } from "@clerk/react";
import { useEntitlement } from "../lib/EntitlementContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  const { isSignedIn, isLoaded } = useAuth();
  const { openSignIn } = useClerk();
  const { status, hasActiveEntitlement, isEntitlementResolved } = useEntitlement();
  const canBuySubscription =
    isLoaded && isSignedIn && status === "ready" && !hasActiveEntitlement;
  const canEnterStudio =
    isLoaded && isSignedIn && isEntitlementResolved && hasActiveEntitlement;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const goToPricing = () => {
    navigate("/");
    setTimeout(() => {
      document.getElementById("pricing")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  const accountReady = isLoaded && isEntitlementResolved;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/6"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 lg:px-16 h-20">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
          <span className="w-9 h-9 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/30 flex items-center justify-center group-hover:bg-[#C5A059]/20 transition-colors">
            <Headphones className="w-4 h-4 text-[#C5A059]" strokeWidth={1.6} />
          </span>
          <span className="font-heading text-lg font-semibold tracking-tight text-white">
            Kiru<span className="text-[#C5A059]">·</span>beezy
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-10 text-sm text-zinc-400 font-medium">
          <button onClick={() => scrollTo("curriculum")} className="hover:text-white transition-colors">
            Curriculum
          </button>
          <button onClick={() => scrollTo("instructor")} className="hover:text-white transition-colors">
            Instructor
          </button>
          {canEnterStudio ? (
            <button onClick={() => navigate("/enter-studio")} className="hover:text-white transition-colors">
              Studio
            </button>
          ) : (
            <button onClick={() => scrollTo("pricing")} className="hover:text-white transition-colors">
              Pricing
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {!accountReady ? (
            <span className="px-4 py-2 text-sm text-zinc-500">Checking...</span>
          ) : !isSignedIn ? (
            <button
              type="button"
              onClick={() => openSignIn()}
              className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/4 px-4 py-2 text-sm font-medium text-zinc-200 transition-all duration-200 hover:border-white/20 hover:bg-white/8 sm:inline-flex"
            >
              <LogIn className="h-4 w-4" />
              Login
            </button>
          ) : canEnterStudio ? (
            <>
              <span className="hidden items-center gap-1.5 rounded-full border border-[#C5A059]/25 bg-[#C5A059]/10 px-3 py-2 text-xs font-medium text-[#E3C985] md:inline-flex">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Active
              </span>
              <button
                data-testid="navbar-enter-studio"
                type="button"
                onClick={() => navigate("/enter-studio")}
                className="inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-4 py-2 text-sm font-semibold text-black transition hover:bg-[#d7b76f]"
              >
                <Headphones className="h-4 w-4" />
                Enter Studio
              </button>
              <UserButton afterSignOutUrl="/" />
            </>
          ) : canBuySubscription ? (
            <>
              <button
                data-testid="navbar-buy-subscription"
                type="button"
                onClick={goToPricing}
                className="inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-4 py-2 text-sm font-semibold text-black transition hover:bg-[#d7b76f]"
              >
                <CreditCard className="h-4 w-4" />
                Buy Now
              </button>
              <UserButton afterSignOutUrl="/" />
            </>
          ) : (
            <UserButton afterSignOutUrl="/" />
          )}
        </div>
      </nav>
    </header>
  );
};
